import { useMemo } from 'react';

import { useShowCauseW_Selector } from '@givebacks/api/core';

import { useAccessControl_Core } from '../accessControl';
import { useFeaturesContext_Core } from '../factory';

const useHasAccessToCompliance = () => {
  const { causeId } = useFeaturesContext_Core();
  const { accessStatus, isLoading } = useAccessControl_Core({
    feature: 'compliance.beta',
    causeId,
  });
  const { data: cause } = useShowCauseW_Selector({ uuid: causeId });

  const isEnabled = useMemo(() => {
    return !!accessStatus || !!cause?.in_district;
  }, [accessStatus, cause?.in_district]);

  return { isEnabled, isLoading };
};

/**
 *  Controls if there should be access granted to the events beta feature.
 */
const useHasAccessToTicketedEventsBeta = () => {
  const { causeId } = useFeaturesContext_Core();
  const { accessStatus, isLoading } = useAccessControl_Core({
    feature: 'ticketedEvents.beta',
    causeId,
  });
  return { isEnabled: accessStatus, isLoading };
};

/**
 *  Controls if there should be access granted to the Contacts beta feature.
 */
const useHasAccessToContacts = () => {
  const { causeId } = useFeaturesContext_Core();
  const { accessStatus, isLoading } = useAccessControl_Core({
    feature: 'contacts.beta',
    causeId,
  });
  return { isEnabled: accessStatus, isLoading };
};

/**
 *  Controls if there should be access granted to the account codes feature.
 *  This accounts for the business logic of
 *
 *  if CoreAPI.cause.is_root || CoreAPI.cause.is_standalone
 *  Returns Access to Account Codes Beta
 *  else if
 *    ComplianceAPI.cause.account_code_on # UI Account Code Settings Toggle
 *     Returns Compliance Cause Access
 */
const useHasAccessToAccountCodes = () => {
  const { causeId } = useFeaturesContext_Core();
  const { accessStatus, isLoading } = useAccessControl_Core({
    feature: 'accountCodes',
    causeId,
  });
  return { isEnabled: accessStatus, isLoading };
};

const useHasAccessToQuickbooks = () => {
  const { causeId } = useFeaturesContext_Core();
  const { accessStatus, isLoading } = useAccessControl_Core({
    feature: 'quickbooks',
    causeId,
  });
  return { isEnabled: accessStatus, isLoading };
};

const useHasAccessToUnitDataBeta = ({
  causeId: _causeId,
}: {
  causeId?: string;
  featureId?: 'unit_data.beta';
} = {}) => {
  const { causeId } = useFeaturesContext_Core();
  const { accessStatus, isLoading } = useAccessControl_Core({
    feature: 'unit_data.beta',
    causeId: _causeId || causeId,
  });
  return { isEnabled: accessStatus, isLoading };
};

/**
 *  Controls if there should be access granted to the pos beta feature.
 */
const useHasAccessToPosBeta = () => {
  const { causeId } = useFeaturesContext_Core();
  const { accessStatus, isLoading } = useAccessControl_Core({
    feature: 'pos.beta',
    causeId,
  });
  return { isEnabled: accessStatus, isLoading };
};

/**
 *  Controls if there should be access granted to the fees beta feature.
 */
const useHasAccessToStudentFeesBeta = () => {
  const { causeId } = useFeaturesContext_Core();
  const { accessStatus, isLoading } = useAccessControl_Core({
    feature: 'student_fees.beta',
    causeId,
  });
  return { isEnabled: accessStatus, isLoading };
};

/**
 *  Controls if there should be access granted to the fee promotion feature.
 */
const useHasAccessToFacultyAccess = () => {
  const { causeId } = useFeaturesContext_Core();
  const { accessStatus, isLoading } = useAccessControl_Core({
    feature: 'faculty_access',
    causeId,
  });
  return { isEnabled: accessStatus, isLoading };
};

/**
 *  Controls if there should be access granted to the fee promotion feature.
 */
const useHasAccessToFeePromotion = () => {
  const { causeId } = useFeaturesContext_Core();
  const { accessStatus, isLoading } = useAccessControl_Core({
    feature: 'fee_promotion',
    causeId,
  });
  return { isEnabled: accessStatus, isLoading };
};

/**
 *  Controls if there should be access granted to the store_tips feature.
 *  Allows passing in a parameter at use-time to override the causeId provided via the FeatureProvider
 */
const useHasAccessToStoreTips = ({
  causeId: _causeId,
}: {
  causeId?: string;
  featureId?: 'store_tips';
} = {}) => {
  const { causeId } = useFeaturesContext_Core();
  const { accessStatus, isLoading } = useAccessControl_Core({
    feature: 'store_tips',
    causeId: _causeId || causeId,
  });
  return { isEnabled: accessStatus, isLoading };
};

/**
 *  Controls if there should be access granted to the directory beta feature.
 */
const useHasAccessToDirectoryBeta = () => {
  const { causeId } = useFeaturesContext_Core();
  const { accessStatus, isLoading } = useAccessControl_Core({
    feature: 'directory.beta',
    causeId,
  });
  return { isEnabled: accessStatus, isLoading };
};

/**
 *  Controls if there should be access granted to the Dues Remittance beta feature.
 *  This should be used anywhere the flag is being used outside of the core page.
 */
const useHasAccessToDuesRemittanceBeta = () => {
  const { causeId } = useFeaturesContext_Core();
  const { accessStatus, isLoading } = useAccessControl_Core({
    feature: 'dues_remittance.beta',
    causeId,
  });
  return { isEnabled: accessStatus, isLoading };
};

/**
 *  Controls if there should be access granted to the product variants feature.
 */
const useHasAccessToProductVariants = () => {
  const { causeId } = useFeaturesContext_Core();
  const { accessStatus, isLoading } = useAccessControl_Core({
    feature: 'product_variants',
    causeId,
  });
  return { isEnabled: accessStatus, isLoading };
};

/**
 *  Controls if there should be access granted to the Sales Tax beta feature.
 */
const useHasAccessToSalesTaxBeta = () => {
  const { causeId } = useFeaturesContext_Core();
  const { accessStatus, isLoading } = useAccessControl_Core({
    feature: 'sales_tax.beta',
    causeId,
  });
  return { isEnabled: accessStatus, isLoading };
};

export {
  useHasAccessToAccountCodes,
  useHasAccessToCompliance,
  useHasAccessToContacts,
  useHasAccessToDirectoryBeta,
  useHasAccessToDuesRemittanceBeta,
  useHasAccessToFacultyAccess,
  useHasAccessToFeePromotion,
  useHasAccessToPosBeta,
  useHasAccessToProductVariants,
  useHasAccessToQuickbooks,
  useHasAccessToSalesTaxBeta,
  useHasAccessToStoreTips,
  useHasAccessToStudentFeesBeta,
  useHasAccessToTicketedEventsBeta,
  useHasAccessToUnitDataBeta,
};
